import React, { useContext, useState, useReducer } from 'react'
import { useField } from 'formik'
import { ThemeContext } from 'styled-components'
import { Box } from 'reflexbox/styled-components'
import AsyncPaginate from 'react-select-async-paginate'

import { Label, ErrorText } from './styles'

const Select = ({ loadOptions, ...props }) => {
  const theme = useContext(ThemeContext)
  const [isFocused, setFocused] = useState(false)
  const [{ value, ...field }, meta, { setValue }] = useField(props)
  let [update, forceUpdate] = useReducer(x => x + 1, 0)
  const isError = meta.touched && meta.error

  const customStyles = {
    control: (provided, state) => {
      const boxShadow = state?.isFocused && theme.shadows.under[1]

      const borderColor = () => {
        if (state.isFocused) return theme.colors.primary
        if (state.isDisabled) return theme.colors.grays.N300
        if (isError) return theme.colors.error
        return theme.colors.grays.N500
      }

      const backgroundColor = theme.colors.white

      return {
        ...provided,
        backgroundColor,
        boxShadow,
        borderRadius: '8px',
        borderColor: borderColor(),
        '&:hover': { borderColor: borderColor() },
      }
    },

    singleValue: provided => ({ ...provided, color: theme.colors.text }),

    input: provided => ({
      ...provided,
      color: theme.colors.text,
      margin: 0,
    }),

    valueContainer: provided => ({ ...provided, padding: ' 7px 32px' }),

    placeholder: (provided, state) => ({
      ...provided,
      margin: 0,
      color: state.isDisabled ? theme.colors.grays.N300 : theme.colors.grays.N400,
    }),

    menu: provided => ({
      ...provided,
      borderRadius: '8px',
      boxShadow: `0 0 0 1px ${theme.colors.primary}, ${theme.shadows.under[1]}`,
      'z-index': 11,
    }),

    menuPortal: provided => ({
      ...provided,
      zIndex: 9999,
    }),

    option: (provided, state) => {
      const styles = {
        ...provided,
        color: theme.colors.text,
        backgroundColor: state.isFocused ? theme.colors.primary : provided.backgroundColor,
        minHeight: '30px',
      }

      if (!state.isSelected) {
        return styles
      }

      return {
        ...styles,
        borderLeft: `4px solid ${theme.colors.primary}`,
        paddingLeft: '8px',
        backgroundColor: 'transparent',
        '&:hover': { backgroundColor: theme.colors.primary },
      }
    },
  }

  return (
    <Box sx={{ position: 'relative' }} {...props}>
      <AsyncPaginate
        {...field}
        {...props}
        onChange={data => {
          setValue(data)
        }}
        value={value}
        isMulti={props.multiple}
        styles={customStyles}
        maxMenuHeight='200px'
        components={{ IndicatorSeparator: null }}
        onFocus={() => {
          setFocused(true)
        }}
        cacheUniq={props.forceUpdate === true ? [update] : false}
        onBlur={() => setFocused(false)}
        isDisabled={props.disabled}
        loadOptions={loadOptions}
        isClearable={props.isClearable === false ? false : true}
        options={props.options}
      />
      <Label focused={isFocused} disabled={props.disabled} error={isError}>
        {props.label}
      </Label>
      {isError && <ErrorText>{meta.error}</ErrorText>}
    </Box>
  )
}

Select.defaultProps = {
  isClearable: true,
  loadOptions: () => {},
}

export default Select
